/**
 * Folder-aware drag-and-drop traversal.
 *
 * Browsers don't populate `DataTransfer.files` when the user drops a
 * folder — we have to walk `DataTransferItem.webkitGetAsEntry()`
 * recursively to surface every file inside. This helper does that walk
 * and returns a flat list of `{ file, relativePath }` entries (caller
 * decides whether to preserve hierarchy or flatten).
 *
 * Hard-capped at `MAX_DROPPED_FILES` per drop. If the traversal would
 * exceed the cap, the helper short-circuits and returns `{ kind:
 * 'too-many', count }` so callers can reject the whole drop atomically
 * (no partial uploads).
 */

export const MAX_DROPPED_FILES = 20;

export interface DroppedEntry {
  file: File;
  /** Forward-slash path relative to the drop root. For a flat file drop
   *  this is just `file.name`; for a folder drop it's
   *  `folder/sub/name.ext`. Never has a leading slash. */
  relativePath: string;
}

export type DropResult =
  | { kind: 'ok'; entries: DroppedEntry[]; hadFolder: boolean }
  | { kind: 'too-many'; count: number };

/**
 * Walk the items in a `DataTransfer` and collect every file, recursing
 * into folders via the FileSystem entry API.
 *
 * Falls back to `dt.files` when:
 *   - the browser doesn't expose `webkitGetAsEntry` (very old engines), or
 *   - none of the items are entries (some browsers omit entries for
 *     drag sources that aren't from the OS file picker / file system).
 */
export async function readDroppedEntries(dt: DataTransfer): Promise<DropResult> {
  const items = dt.items;
  const entries: FileSystemEntry[] = [];
  if (items && typeof items[0]?.webkitGetAsEntry === 'function') {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      // `webkitGetAsEntry` returns null for non-file items (e.g. text
      // drag payloads) — skip them silently.
      const entry = item.webkitGetAsEntry?.();
      if (entry) entries.push(entry);
    }
  }

  if (entries.length === 0) {
    const flat = Array.from(dt.files ?? []);
    if (flat.length > MAX_DROPPED_FILES) {
      return { kind: 'too-many', count: flat.length };
    }
    return {
      kind: 'ok',
      entries: flat.map((file) => ({ file, relativePath: file.name })),
      hadFolder: false,
    };
  }

  const collected: DroppedEntry[] = [];
  let hadFolder = false;
  // Eagerly bail out the moment we see we'll exceed the cap. The
  // partial walk so far is discarded by the caller — we never touch
  // the VFS / composer state until traversal succeeds.
  let overflowed = false;
  let overflowCount = 0;

  const walk = async (entry: FileSystemEntry, prefix: string): Promise<void> => {
    if (overflowed) return;
    if (entry.isFile) {
      const file = await fileFromEntry(entry as FileSystemFileEntry);
      const relativePath = prefix ? `${prefix}/${file.name}` : file.name;
      collected.push({ file, relativePath });
      if (collected.length > MAX_DROPPED_FILES) {
        overflowed = true;
        // Best-effort count: at least cap+1. The exact total isn't worth
        // a full walk just to message — the user knows roughly what they
        // dropped, and "more than 20" is the actionable bit.
        overflowCount = collected.length;
      }
      return;
    }
    hadFolder = true;
    const dir = entry as FileSystemDirectoryEntry;
    const nextPrefix = prefix ? `${prefix}/${dir.name}` : dir.name;
    const children = await readAllChildren(dir);
    for (const child of children) {
      if (overflowed) return;
      await walk(child, nextPrefix);
    }
  };

  for (const entry of entries) {
    if (overflowed) break;
    await walk(entry, '');
  }

  if (overflowed) {
    return { kind: 'too-many', count: overflowCount };
  }
  return { kind: 'ok', entries: collected, hadFolder };
}

/** Promisified `FileSystemFileEntry.file()`. */
function fileFromEntry(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => {
    entry.file(resolve, reject);
  });
}

/**
 * Drain `createReader().readEntries()` until it returns an empty batch.
 * The API hard-caps each call at ~100 children, so we must loop — a
 * single call is silently incomplete for larger folders.
 */
function readAllChildren(dir: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const reader = dir.createReader();
    const all: FileSystemEntry[] = [];
    const readBatch = () => {
      reader.readEntries((batch) => {
        if (batch.length === 0) {
          resolve(all);
          return;
        }
        for (const child of batch) all.push(child);
        readBatch();
      }, reject);
    };
    readBatch();
  });
}
